import { ViewBooking, ViewBookingStatusEnum } from "collins-js-sdk";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { BookingLabelsEnum } from "../types/bookingEnums";
import { getTimeFromString } from "./booking";
import { stageIs } from "./bookingStage";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

export const completeBookingsFilter = (booking: ViewBooking) =>
  booking.status === "complete" &&
  booking.currentStage != "left" &&
  (booking.actualGuests ?? 0) > 0;

// A booking is treated as cancelled when it has been rejected/customer
// cancelled (status Rejected) or explicitly moved to a "cancelled" stage.
export const cancelledBookingsFilter = (booking: ViewBooking) =>
  !stageIs(booking, "no-show") &&
  (booking.status === ViewBookingStatusEnum.Rejected ||
    stageIs(booking, "cancelled"));

export const noShowBookingsFilter = (booking: ViewBooking) =>
  stageIs(booking, "no-show");

// Mirrors the shared BookingsGrid "hasBookingLeft" rule
// (currentStage === "left" || (actualGuests ?? 0) === 0) so the Closed list and
// the grid agree on which bookings are treated as left, minus the cancelled and
// no-show bookings which are surfaced in their own sections. A missing/blank
// guest count is treated as 0 (same as the grid) so a booking greyed as left on
// the grid always appears in this section too.
export const leftBookingsFilter = (booking: ViewBooking) =>
  booking.status !== ViewBookingStatusEnum.Rejected &&
  !stageIs(booking, "cancelled") &&
  !stageIs(booking, "no-show") &&
  (stageIs(booking, "left") || (booking.actualGuests ?? 0) === 0);

// Union of the three "closed" states surfaced under the Closed tab.
export const closedBookingsFilter = (booking: ViewBooking) =>
  leftBookingsFilter(booking) ||
  cancelledBookingsFilter(booking) ||
  noShowBookingsFilter(booking);

export const waitlistBookingsFilter = (booking: ViewBooking) =>
  (booking.status === "in_progress" || booking.status === "new") &&
  booking.waitlisted &&
  booking.currentStage !== "left" &&
  !stageIs(booking, "no-show") &&
  !stageIs(booking, "cancelled") &&
  booking.actualGuests !== 0;

export const openBookingsFilter = (booking: ViewBooking) =>
  (booking.status === "in_progress" || booking.status === "new") &&
  !booking.waitlisted &&
  booking.currentStage !== "left" &&
  !stageIs(booking, "cancelled") &&
  !stageIs(booking, "no-show");

export const morningFilter = (booking: ViewBooking) =>
  booking.time &&
  dayjs(getTimeFromString(booking.time)).isSameOrBefore(
    dayjs().hour(11).minute(59)
  );

export const afternoonFilter = (booking: ViewBooking) =>
  booking.time &&
  dayjs(getTimeFromString(booking.time)).isSameOrAfter(
    dayjs().hour(12).minute(0)
  ) &&
  dayjs(getTimeFromString(booking.time)).isSameOrBefore(
    dayjs().hour(15).minute(59)
  );

export const eveningFilter = (booking: ViewBooking) =>
  booking.time &&
  dayjs(getTimeFromString(booking.time)).isSameOrAfter(
    dayjs().hour(16).minute(0)
  ) &&
  dayjs(getTimeFromString(booking.time)).isBefore(dayjs().hour(23).minute(59));

export const autoConfirmedFilter = (booking: ViewBooking) =>
  !!booking.autoConfirmed;

export const customerRequestsFilter = (booking: ViewBooking) =>
  booking.labels?.[0] === BookingLabelsEnum.PackagesRequested;

export const runSheetNotesFilter = (booking: ViewBooking) =>
  booking.comments?.length !== 0;

export const doubleConfirmedFilter = (booking: ViewBooking) =>
  booking.doubleConfirmed;

export const notDoubleConfirmedFilter = (booking: ViewBooking) =>
  !booking.doubleConfirmed;

export const collinsCallChanneFilter = (booking: ViewBooking) =>
  booking.channel === "collins-call";
